// PaymentSettingsCard — compact "what {players|sub-agents} see" QR card
// for the admin SPA. Replaces the old always-exposed PaymentSettingsPanel
// form on /people/players/requests and /people/agents/requests.
//
// FLAW 1 fix (2026-05-16): The previous PaymentSettingsPanel exposed
// every input (UPI, WhatsApp, support phone, display note, QR upload,
// is-active toggle) directly inline at the top of the requests hub.
// On phones this consumed roughly the entire viewport before the admin
// could even SEE the queue they came to triage. The admin-frontend now
// follows the same compact pattern the agent SPA shipped in Phase 9 —
// a tiny read-only summary card with a single "Edit" button that opens
// a focused modal.
//
// ─── Pattern ──────────────────────────────────────────────────────
//   Read-only summary card:
//     • QR thumbnail (or placeholder) on the left
//     • Live / Paused / Not-set status pill floating on the QR
//     • UPI handle below the label
//     • WhatsApp + support-phone chips
//     • Single "Edit" button that opens PaymentSettingsEditModal
//
//   Modal:
//     • All the fields the old inline form had (UPI, WhatsApp, phone,
//       display note, is_active, QR upload + remove)
//     • Same PATCH /admin/payment-settings endpoint
//     • Same /qr and /subagent-qr upload paths
//
// The card is `audience`-aware: 'player' reads/writes the
// player-facing column set (upi_id, qr_image_url, whatsapp_number,
// support_phone, display_note), 'subagent' reads/writes the
// subagent_* twin set (migrations 065 + 067). Backend already routes
// both via the same /admin/payment-settings endpoints — we just send
// the right key per audience.
//
// ─── Why two audience-routed cards on one hub? ────────────────────
// The legacy /finance/settings page rendered BOTH at once. The new
// /people/players/requests hub uses audience='player' only; the
// /people/agents/requests hub uses audience='subagent' only — so each
// hub is laser-focused on the queue it serves. Admins navigating both
// queues see the same compact card layout in both places, which makes
// the mental model of "what each counterparty sees" instant to recall.

import { FormEvent, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { compressForUpload } from '@baaligames/shared-ui/lib/imageCompress';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { INPUT_CLS } from './fieldStyles';

type Cfg = {
  upi_id: string | null;
  qr_image_url: string | null;
  whatsapp_number: string | null;
  support_phone: string | null;
  display_note: string | null;
  subagent_upi_id: string | null;
  subagent_qr_image_url: string | null;
  subagent_whatsapp_number: string | null;
  subagent_support_phone: string | null;
  subagent_display_note: string | null;
  is_active: number;
} | null;

type Audience = 'player' | 'subagent';

type FieldMap = {
  upiKey: 'upi_id' | 'subagent_upi_id';
  qrKey:  'qr_image_url' | 'subagent_qr_image_url';
  whatsappKey: 'whatsapp_number' | 'subagent_whatsapp_number';
  phoneKey:    'support_phone' | 'subagent_support_phone';
  noteKey:     'display_note' | 'subagent_display_note';
  qrEndpoint:  'qr' | 'subagent-qr';
  qrResponseKey: 'qr_image_url' | 'subagent_qr_image_url';
};

const FIELD_MAP_FOR: Record<Audience, FieldMap> = {
  player:   { upiKey: 'upi_id',
              qrKey: 'qr_image_url',
              whatsappKey: 'whatsapp_number',
              phoneKey: 'support_phone',
              noteKey: 'display_note',
              qrEndpoint: 'qr',
              qrResponseKey: 'qr_image_url' },
  subagent: { upiKey: 'subagent_upi_id',
              qrKey: 'subagent_qr_image_url',
              whatsappKey: 'subagent_whatsapp_number',
              phoneKey: 'subagent_support_phone',
              noteKey: 'subagent_display_note',
              qrEndpoint: 'subagent-qr',
              qrResponseKey: 'subagent_qr_image_url' },
};

const AUDIENCE_THEME: Record<Audience, {
  ribbonText: string;
  captionText: string;
  accentClass: string;       // "for sub-agents" caption / inline highlights
  fillClass: string;         // bold solid pill at top of QR
  outlineClass: string;      // outlined "for X" caption under QR
  editBtnClass: string;
}> = {
  player: {
    ribbonText:  'My QR code',
    captionText: 'For my players',
    accentClass: 'text-sky-700 dark:text-sky-300',
    fillClass:   'bg-sky-600 text-white dark:bg-sky-500 dark:text-white shadow-[0_1px_3px_rgba(14,165,233,0.4)]',
    outlineClass:'bg-sky-50 dark:bg-sky-500/15 text-sky-700 dark:text-sky-200 border-sky-300/70 dark:border-sky-400/40',
    editBtnClass:'bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white shadow-[0_2px_10px_rgba(14,165,233,0.45)]',
  },
  subagent: {
    ribbonText:  'My QR code',
    captionText: 'For my sub-agents',
    accentClass: 'text-emerald-700 dark:text-emerald-300',
    fillClass:   'bg-emerald-600 text-white dark:bg-emerald-500 dark:text-white shadow-[0_1px_3px_rgba(16,185,129,0.4)]',
    outlineClass:'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-300/70 dark:border-emerald-400/40',
    editBtnClass:'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white shadow-[0_2px_10px_rgba(16,185,129,0.45)]',
  },
};

export default function PaymentSettingsCard({ audience }: { audience: Audience }) {
  const auth = useAdminAuth();
  const canEdit = auth.canDo('payment_settings', 'edit');
  const isAdmin = auth.admin?.role === 'admin';

  // Hooks before any early return — Rules of Hooks compliance.
  const [cfg, setCfg] = useState<Cfg>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  // 2026-05-18 — Architecture cleanup: migration 090 dropped the
  // admin's player-facing payment columns (upi_id/qr_image_url/…).
  // Players now ONLY transact with their direct agent — admin no
  // longer publishes a player-facing QR. PlayerRequestsPanel still
  // mounts <PaymentSettingsCard audience="player"/> because for the
  // AGENT role that card is legitimate (agents do have a player
  // audience). For ADMIN role we render NOTHING for audience="player"
  // — there's no such config to surface and showing it misleads
  // admin into configuring a QR that never reaches a player.
  //
  // The 'subagent' audience card stays for both roles: agents
  // legitimately address their sub-agent downline through that
  // variant, and for admin role migration 090 renamed subagent_* to
  // plain — so for admin we transparently rewrite the field-map keys
  // to target the renamed plain columns.
  const hide = isAdmin && audience === 'player';

  const baseFieldMap = FIELD_MAP_FOR[audience];
  const fieldMap: FieldMap = (isAdmin && audience === 'subagent')
    ? {
        upiKey: 'upi_id', qrKey: 'qr_image_url',
        whatsappKey: 'whatsapp_number', phoneKey: 'support_phone',
        noteKey: 'display_note',
        qrEndpoint: 'qr',                 // admin's single QR endpoint
        qrResponseKey: 'qr_image_url',
      } as FieldMap
    : baseFieldMap;
  const theme    = AUDIENCE_THEME[audience];

  async function load() {
    setErr(null);
    try {
      const r = await adminApi.get<{ config: Cfg }>('/admin/payment-settings');
      setCfg(r.config);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setLoading(false); }
  }
  useEffect(() => {
    // Skip the fetch entirely when this card is hidden — no point
    // hitting the backend for a config the admin can't configure.
    if (hide) { setLoading(false); return; }
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hide]);

  // Hidden after all hooks have been called (Rules of Hooks).
  if (hide) return null;

  const audienceUpi = cfg?.[fieldMap.upiKey] ?? null;
  const audienceQr  = cfg?.[fieldMap.qrKey]  ?? null;
  // Sub-agent contacts fall back to the player-facing pair when unset
  // (matches server-side fallback behaviour).
  const audienceWhatsapp = audience === 'subagent'
    ? (cfg?.subagent_whatsapp_number || cfg?.whatsapp_number || null)
    : cfg?.whatsapp_number || null;
  const audiencePhone = audience === 'subagent'
    ? (cfg?.subagent_support_phone || cfg?.support_phone || null)
    : cfg?.support_phone || null;
  const live = !!audienceUpi && cfg?.is_active === 1;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl overflow-hidden
                   bg-white dark:bg-arena-700/40
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                   shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
      >
        <div className="p-3 sm:p-4">
          <div className="flex items-start gap-3">
            {/* QR column — ribbon + image + caption + floating status */}
            <div className="shrink-0 flex flex-col items-center gap-1.5">
              <div className={`px-2 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap ${theme.fillClass}`}>
                {theme.ribbonText}
              </div>

              <div className="relative">
                {audienceQr ? (
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden
                                  bg-white p-1.5 ring-1 ring-arena-200 dark:ring-arena-600/60">
                    <img src={audienceQr} alt="payment QR code" className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl grid place-items-center
                                  bg-arena-50/60 dark:bg-arena-900/40
                                  border-2 border-dashed border-arena-300/60 dark:border-arena-600/60">
                    <Icon name="qr_code_2" size={28} className="text-arena-400 dark:text-neon-200/35" />
                  </div>
                )}
                <div className="absolute -top-1.5 -right-1.5 z-10">
                  <StatusPill live={live} hasCfg={!!cfg} loading={loading} />
                </div>
              </div>

              <div className={`px-2 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap border ${theme.outlineClass}`}>
                {theme.captionText}
              </div>
            </div>

            {/* Details column */}
            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
              <div className="inline-flex items-center gap-1.5 min-w-0">
                <Icon name="qr_code" size={12} className="text-arena-500 dark:text-neon-200/65 shrink-0" />
                <span className={`text-[10px] uppercase tracking-wider font-bold ${theme.accentClass}`}>
                  {audience === 'subagent'
                    ? 'What your sub-agents see'
                    : 'What your players see'}
                </span>
              </div>

              <div className="text-[14px] sm:text-[15px] font-bold text-arena-900 dark:text-white font-mono break-all leading-tight">
                {audienceUpi || (loading ? '…' : 'No UPI set')}
              </div>

              {(audienceWhatsapp || audiencePhone) && (
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                  {audienceWhatsapp && (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]
                                     bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200
                                     dark:bg-emerald-400/15 dark:text-emerald-200 dark:ring-emerald-400/30
                                     font-mono">
                      <Icon name="chat" size={11} />{audienceWhatsapp}
                    </span>
                  )}
                  {audiencePhone && (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]
                                     bg-cyan-50 text-cyan-800 ring-1 ring-inset ring-cyan-200
                                     dark:bg-cyan-400/15 dark:text-cyan-200 dark:ring-cyan-400/30
                                     font-mono">
                      <Icon name="call" size={11} />{audiencePhone}
                    </span>
                  )}
                </div>
              )}

              {err && <div className="text-[11px] text-rose-700 dark:text-rose-300">{err}</div>}

              <div className="flex items-center gap-2 mt-1">
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                                text-[12px] font-bold transition active:scale-95
                                ${theme.editBtnClass}`}
                  >
                    <Icon name="edit" size={13} />
                    {audienceQr || audienceUpi ? 'Edit' : 'Set up'}
                  </button>
                ) : (
                  <span className="text-[11px] italic text-arena-500 dark:text-neon-200/55">
                    View only — needs payment_settings:edit
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {editing && (
          <PaymentSettingsEditModal
            cfg={cfg}
            audience={audience}
            fieldMap={fieldMap}
            theme={theme}
            onClose={() => setEditing(false)}
            onSaved={(next) => { setCfg(next); setEditing(false); }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function StatusPill({ live, hasCfg, loading }: { live: boolean; hasCfg: boolean; loading: boolean }) {
  const base = `shrink-0 inline-flex items-center gap-1.5 rounded-full
                px-2 py-0.5 text-[9.5px] font-extrabold tracking-wide`;
  if (loading) {
    return <span className={`${base} bg-arena-500 text-white shadow-[0_2px_6px_rgba(0,0,0,0.3)]`}>…</span>;
  }
  if (live) {
    return (
      <span className={`${base} bg-emerald-500 text-white shadow-[0_2px_8px_rgba(16,185,129,0.55)]`}>
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
        Live
      </span>
    );
  }
  if (!hasCfg) {
    return <span className={`${base} bg-rose-500 text-white shadow-[0_2px_8px_rgba(244,63,94,0.55)]`}>Not set</span>;
  }
  return <span className={`${base} bg-amber-400 text-amber-950 shadow-[0_2px_8px_rgba(251,191,36,0.6)]`}>Paused</span>;
}

// ─── Edit modal ────────────────────────────────────────────────────

function PaymentSettingsEditModal({
  cfg, audience, fieldMap, theme, onClose, onSaved,
}: {
  cfg: Cfg;
  audience: Audience;
  fieldMap: FieldMap;
  theme: (typeof AUDIENCE_THEME)[Audience];
  onClose: () => void;
  onSaved: (next: Cfg) => void;
}) {
  const [upi, setUpi]           = useState((cfg?.[fieldMap.upiKey] ?? '') as string);
  const [whatsapp, setWhatsapp] = useState((cfg?.[fieldMap.whatsappKey] ?? '') as string);
  const [phone, setPhone]       = useState((cfg?.[fieldMap.phoneKey] ?? '') as string);
  const [note, setNote]         = useState((cfg?.[fieldMap.noteKey] ?? '') as string);
  const [active, setActive]     = useState(cfg?.is_active !== 0);
  const [qrUrl, setQrUrl]       = useState((cfg?.[fieldMap.qrKey] ?? null) as string | null);
  const [busy, setBusy]         = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr]           = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const payload: Record<string, unknown> = {};
      payload[fieldMap.upiKey]      = upi.trim()      || null;
      payload[fieldMap.whatsappKey] = whatsapp.trim() || null;
      payload[fieldMap.phoneKey]    = phone.trim()    || null;
      payload[fieldMap.noteKey]     = note.trim()     || null;
      // is_active is owner-level (single toggle for both audiences).
      // Only include it when editing the player-facing variant so the
      // sub-agent edit modal can't accidentally pause the player flow.
      if (audience === 'player') payload.is_active = active;
      const r = await adminApi.patch<{ config: Cfg }>('/admin/payment-settings', payload);
      const merged: Cfg = r.config
        ? ({ ...r.config, [fieldMap.qrKey]: qrUrl } as Cfg)
        : r.config;
      onSaved(merged);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  async function uploadQr(rawFile: File) {
    setUploading(true); setErr(null);
    try {
      const file = await compressForUpload(rawFile, { preset: 'payment_qr' });
      const fd = new FormData();
      fd.append('qr', file);
      const r = await adminApi.postForm<Record<string, string>>(
        `/admin/payment-settings/${fieldMap.qrEndpoint}`, fd
      );
      setQrUrl(r[fieldMap.qrResponseKey] || null);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setUploading(false); }
  }

  async function removeQr() {
    if (!qrUrl) return;
    if (!confirm(audience === 'subagent'
      ? 'Remove the sub-agent QR? Sub-agents will fall back to your player-facing QR.'
      : 'Remove the QR image? Players will see fallback contact info only.')) return;
    setUploading(true); setErr(null);
    try {
      await adminApi.del(`/admin/payment-settings/${fieldMap.qrEndpoint}`);
      setQrUrl(null);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setUploading(false); }
  }

  // 2026-05-20 — aliased to the canonical INPUT_CLS so the dark-mode
  // fill + ring fix in components/fieldStyles.ts applies here too.
  const inputCls = INPUT_CLS;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[8000] grid place-items-start sm:place-items-center
                 p-3 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={busy || uploading ? undefined : onClose}
      role="dialog" aria-modal="true"
    >
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.97 }}
        transition={{ duration: 0.18 }}
        className="w-full max-w-md rounded-3xl
                   bg-white dark:bg-arena-900
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/50
                   shadow-[0_20px_60px_rgba(0,0,0,0.35)]
                   overflow-hidden my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 ring-1 ring-inset ring-arena-200/40 dark:ring-arena-700/40
                        bg-arena-50/40 dark:bg-arena-900/40
                        flex items-center gap-2">
          <Icon name="qr_code_2" size={18} className={theme.accentClass} />
          <h3 className="text-[15px] font-bold text-arena-900 dark:text-white">
            {audience === 'subagent' ? 'Sub-agent payment settings' : 'Player payment settings'}
          </h3>
          <button onClick={onClose} disabled={busy || uploading}
            className="ml-auto grid place-items-center w-7 h-7 rounded-full hover:bg-arena-100/60 dark:hover:bg-arena-700/55 transition disabled:opacity-40"
            aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>

        <form onSubmit={save} className="px-5 py-4 space-y-3">
          {/* QR uploader — keep the upload affordance inline so admin
              isn't bounced through a second sub-modal just to swap the
              QR image. */}
          <div className="flex items-start gap-3">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden
                            bg-white p-1.5 ring-1 ring-arena-200 dark:ring-arena-600/60 shrink-0">
              {qrUrl ? (
                <img src={qrUrl} alt="QR preview" className="w-full h-full object-contain" />
              ) : (
                <div className="grid place-items-center w-full h-full
                                border-2 border-dashed border-arena-300/60 dark:border-arena-600/60 rounded-md">
                  <Icon name="qr_code_2" size={26} className="text-arena-400 dark:text-neon-200/35" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadQr(f); }} />
              <button type="button" disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full
                            text-[12px] font-bold transition active:scale-95 disabled:opacity-60
                            ${theme.editBtnClass}`}>
                {uploading ? <Icon name="progress_activity" size={13} className="animate-spin" /> : <Icon name="upload" size={13} />}
                {qrUrl ? 'Replace QR' : 'Upload QR'}
              </button>
              {qrUrl && (
                <button type="button" onClick={() => void removeQr()} disabled={uploading}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full
                             text-[12px] font-semibold ring-1 ring-inset ring-rose-300/60 dark:ring-rose-400/30
                             text-rose-700 dark:text-rose-300
                             hover:bg-rose-50 dark:hover:bg-rose-500/15 disabled:opacity-60">
                  <Icon name="delete" size={13} /> Remove
                </button>
              )}
            </div>
          </div>

          <Field label="UPI ID">
            <input className={inputCls + ' font-mono'} value={upi} onChange={(e) => setUpi(e.target.value)}
              placeholder="yourname@upi" />
          </Field>
          <Field label="WhatsApp number">
            <input className={inputCls + ' font-mono'} value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="+91…" />
          </Field>
          <Field label="Support phone">
            <input className={inputCls + ' font-mono'} value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+91…" />
          </Field>
          <Field label="Display note (optional)">
            <textarea className={inputCls} rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Shown above the QR. Keep it short." />
          </Field>

          {/* is_active toggle — only shown in the player-facing modal.
              The sub-agent variant doesn't get its own pause switch
              (the owner-level flag is shared with the player flow). */}
          {audience === 'player' && (
            <label className="flex items-center gap-2 select-none">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
                className="w-4 h-4 accent-violet-500" />
              <span className="text-[13px] text-arena-700 dark:text-neon-200/85">
                Active — players can submit recharge requests
              </span>
            </label>
          )}

          {err && (
            <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200 text-sm px-3 py-2">
              {err}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} disabled={busy}
              className="px-4 py-2 rounded-full text-[13px] font-semibold
                         bg-white dark:bg-arena-700/40
                         ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                         text-arena-700 dark:text-neon-200/75
                         hover:bg-arena-50 dark:hover:bg-arena-700/60">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              className={`px-4 py-2 rounded-full text-[13px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50 ${theme.editBtnClass}`}>
              {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="check" size={14} />}
              Save settings
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">{label}</span>
      {children}
    </label>
  );
}
